// Session management utility
const SESSION_TIMEOUT_HOURS = 5;
const LOGIN_TIME_KEY = 'LOGIN_TIME';
const API_KEY = 'API_KEY';

export class SessionManager {
  /**
   * Set login time in localStorage when user logs in
   */
  static setLoginTime(): void {
    const currentTime = new Date().getTime();
    localStorage.setItem(LOGIN_TIME_KEY, currentTime.toString());
  }

  /**
   * Get login time from localStorage
   */
  static getLoginTime(): number | null {
    const loginTime = localStorage.getItem(LOGIN_TIME_KEY);
    return loginTime ? parseInt(loginTime, 10) : null;
  }

  /**
   * Check if session is expired (more than 5 hours)
   */
  static isSessionExpired(): boolean {
    const loginTime = this.getLoginTime();
    if (!loginTime) {
      return true; // No login time means session is expired
    }

    const currentTime = new Date().getTime();
    const timeDifference = currentTime - loginTime;
    const hoursElapsed = timeDifference / (1000 * 60 * 60); // Convert to hours

    return hoursElapsed > SESSION_TIMEOUT_HOURS;
  }

  /**
   * Check if user is authenticated and session is valid
   */
  static isAuthenticated(): boolean {
    const accessToken =
      sessionStorage.getItem(API_KEY) || localStorage.getItem(API_KEY);
    return !!accessToken && !this.isSessionExpired();
  }

  /**
   * Clear session data (logout)
   */
  static clearSession(): void {
    localStorage.removeItem(LOGIN_TIME_KEY);
    localStorage.removeItem(API_KEY);
    sessionStorage.removeItem(API_KEY);
  }

  /**
   * Get remaining session time in minutes
   */
  static getRemainingSessionTime(): number {
    const loginTime = this.getLoginTime();
    if (!loginTime) {
      return 0;
    }

    const currentTime = new Date().getTime();
    const timeDifference = currentTime - loginTime;
    const hoursElapsed = timeDifference / (1000 * 60 * 60);
    const remainingHours = SESSION_TIMEOUT_HOURS - hoursElapsed;
    
    return Math.max(0, remainingHours * 60); // Return in minutes
  }

  /**
   * Extend session (reset login time)
   */
  static extendSession(): void {
    this.setLoginTime();
  }
}